import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { TBookCover, TBookWrapping } from '@shared/jp/product';
import { TBookAlias } from '@shared/jp/product';
import type { IBookPreview, IPrice } from '@shared/jp/data/products';
import {
  TPhoto,
  INotice,
  includeShippingToBookPrice,
} from '@shared/common/data/products';
import { TPrices } from '@shared/jp/data/products';
import { INPUT_VALUE_COVER_TYPE_SOFT } from '@shared/common/product';
import { slideY } from '@studiobuki/web-core';
import { MediaService } from '@studiobuki/web-core/lib/media';
import { DiscountService } from '@studiobuki/web-core/lib/discount';
import { map } from 'rxjs/operators';
import { getDiscount, getShippingDiscount } from '@shared/jp/discount/utils';
import { IShipping } from '@shared/jp/data/shipping';
import type { TShippingMethod } from '@shared/jp/shipping/interfaces';
// import { Logger } from '@shared/common/logger';

// const log = new Logger('SectionGiftBooksComponent -> BookComponent');

@Component({
  selector: 'app-book',
  templateUrl: './book.component.html',
  styleUrls: ['./book.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideY()],
})
export class BookComponent {
  @Input() selected = false;

  @Input() alias!: TBookAlias;

  @Input() photo!: TPhoto;

  @Input() caption!: IBookPreview['caption'];

  @Input() summary!: IBookPreview['summary'];

  @Input() notice!: INotice;

  @Input() prices!: TPrices<TBookCover>;

  @Input() shippingIncluded!: IShipping;

  @Input() covers!: IBookPreview['options'];

  @Output() bookClick = new EventEmitter<void>();

  @Input() set cover(value: TBookCover | null) {
    if (value && this._cover$.value !== value) {
      this._cover$.next(value);
      this.coverChange.emit(value);
    }
  }

  @Output() coverChange = new EventEmitter<TBookCover>();

  get minPrice(): IPrice {
    const coverPrice = this.prices[0];

    return includeShippingToBookPrice(
      (coverPrice as Exclude<typeof coverPrice, undefined>)[1],
      this.shippingIncluded,
    );
  }

  get coversArr() {
    return Object.values(this.covers);
  }

  private _cover$ = new BehaviorSubject<TBookCover>(
    INPUT_VALUE_COVER_TYPE_SOFT,
  );

  public cover$ = this._cover$.asObservable();

  private _wrapping$ = new BehaviorSubject<TBookWrapping>('standart');

  public wrapping$ = this._wrapping$.asObservable();

  private _hover$ = new BehaviorSubject(false);

  public hover$ = this._hover$.asObservable();

  public discount$ = this._discount.activeDiscountCampaign$.pipe(
    map((_dC) => _dC && getDiscount(_dC, this.alias)),
  );

  public shippingDiscount$ = this._discount.activeDiscountCampaign$.pipe(
    map((_dC) => {
      const shipping = _dC && this.minPrice.shippingIncluded;

      return (
        shipping && getShippingDiscount(_dC, shipping.id as TShippingMethod)
      );
    }),
  );

  public readonly includeShippingToBookPrice = includeShippingToBookPrice;

  constructor(
    public media: MediaService,
    private _discount: DiscountService,
  ) {}

  public onBookClick(): void {
    this.bookClick.emit();
  }

  public onCoverClick(cover: TBookCover): void {
    this.cover = cover;
  }

  public onMouseenter(): void {
    this._hover$.next(true);
  }

  public onMouseleave(): void {
    this._hover$.next(false);
  }
}
